/**
 * Expressions mathématiques saisies par les enseignants (« a*x^2 + b », « 2sin(x) »).
 * Analyse et évaluation sans `eval` ni `Function` : seule une petite grammaire est acceptée,
 * bornée en longueur et en profondeur. Le même code tourne sur le serveur (validation à
 * l'enregistrement) et dans le lecteur de leçon (dessin des courbes, hors ligne).
 */

export const EXPR_LIMITS = { length: 200, nodes: 200, depth: 30 } as const;

type Node =
  | { t: "num"; v: number }
  | { t: "var"; name: string }
  | { t: "neg"; a: Node }
  | { t: "bin"; op: "+" | "-" | "*" | "/" | "^"; a: Node; b: Node }
  | { t: "call"; fn: string; a: Node };

const FUNCTIONS: Record<string, (x: number) => number> = {
  sin: Math.sin, cos: Math.cos, tan: Math.tan, sqrt: Math.sqrt, abs: Math.abs,
  exp: Math.exp, ln: Math.log, log: Math.log10,
};
const CONSTANTS: Record<string, number> = { pi: Math.PI, e: Math.E };

type Tok = { k: "num"; v: number; at: number } | { k: "id"; v: string; at: number } | { k: "op"; v: string; at: number };

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === " " || c === "\t") { i++; continue; }
    if (/[0-9.]/.test(c)) {
      const m = /^(\d+(\.\d*)?|\.\d+)/.exec(src.slice(i));
      if (!m) throw new ExprError(`Nombre mal écrit à la position ${i + 1}`);
      out.push({ k: "num", v: Number(m[0]), at: i });
      i += m[0].length;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      const m = /^[a-zA-Z_]\w*/.exec(src.slice(i))!;
      out.push({ k: "id", v: m[0], at: i });
      i += m[0].length;
      continue;
    }
    if ("+-*/^()".includes(c)) { out.push({ k: "op", v: c, at: i }); i++; continue; }
    if (c === "×" || c === "·") { out.push({ k: "op", v: "*", at: i }); i++; continue; }
    if (c === "²" || c === "³") { out.push({ k: "op", v: "^", at: i }, { k: "num", v: c === "²" ? 2 : 3, at: i }); i++; continue; }
    if (c === "−") { out.push({ k: "op", v: "-", at: i }); i++; continue; }
    if (c === ",") throw new ExprError("Utilisez le point comme séparateur décimal (2.5 et non 2,5)");
    throw new ExprError(`Caractère non autorisé : « ${c} »`);
  }
  return out;
}

export class ExprError extends Error {}

function parse(src: string): Node {
  if (src.length > EXPR_LIMITS.length) throw new ExprError(`Expression trop longue (${EXPR_LIMITS.length} caractères au plus)`);
  const toks = tokenize(src);
  if (toks.length === 0) throw new ExprError("Expression vide");
  let p = 0;
  let nodes = 0;
  const peek = () => toks[p];
  const isOp = (v: string) => peek()?.k === "op" && peek()!.v === v;
  const node = <T extends Node>(n: T): T => { if (++nodes > EXPR_LIMITS.nodes) throw new ExprError("Expression trop complexe"); return n; };

  function expr(d: number): Node {
    if (d > EXPR_LIMITS.depth) throw new ExprError("Expression trop imbriquée");
    let a = term(d + 1);
    while (isOp("+") || isOp("-")) { const op = toks[p++].v as "+" | "-"; a = node({ t: "bin", op, a, b: term(d + 1) }); }
    return a;
  }
  function term(d: number): Node {
    let a = unary(d + 1);
    for (;;) {
      if (isOp("*") || isOp("/")) { const op = toks[p++].v as "*" | "/"; a = node({ t: "bin", op, a, b: unary(d + 1) }); continue; }
      // Multiplication implicite : 2x, 3(x+1), (x+1)(x-1), 2sin(x). Jamais entre deux lettres (« ab » serait ambigu).
      const n = peek();
      const prevIsNumOrParen = toks[p - 1] && (toks[p - 1].k === "num" || (toks[p - 1].k === "op" && toks[p - 1].v === ")"));
      if (n && prevIsNumOrParen && (n.k === "id" || (n.k === "op" && n.v === "("))) { a = node({ t: "bin", op: "*", a, b: power(d + 1) }); continue; }
      return a;
    }
  }
  function unary(d: number): Node {
    if (d > EXPR_LIMITS.depth) throw new ExprError("Expression trop imbriquée");
    if (isOp("-")) { p++; return node({ t: "neg", a: unary(d + 1) }); }
    if (isOp("+")) { p++; return unary(d + 1); }
    return power(d + 1);
  }
  function power(d: number): Node {
    const base = primary(d + 1);
    if (isOp("^")) { p++; return node({ t: "bin", op: "^", a: base, b: unary(d + 1) }); }
    return base;
  }
  function primary(d: number): Node {
    if (d > EXPR_LIMITS.depth) throw new ExprError("Expression trop imbriquée");
    const t = peek();
    if (!t) throw new ExprError("Expression incomplète");
    if (t.k === "num") { p++; return node({ t: "num", v: t.v }); }
    if (t.k === "id") {
      p++;
      if (FUNCTIONS[t.v]) {
        if (!isOp("(")) throw new ExprError(`${t.v} doit être suivi de parenthèses : ${t.v}(x)`);
        p++;
        const a = expr(d + 1);
        if (!isOp(")")) throw new ExprError("Parenthèse fermante manquante");
        p++;
        return node({ t: "call", fn: t.v, a });
      }
      if (t.v in CONSTANTS) return node({ t: "num", v: CONSTANTS[t.v] });
      return node({ t: "var", name: t.v });
    }
    if (t.v === "(") {
      p++;
      const a = expr(d + 1);
      if (!isOp(")")) throw new ExprError("Parenthèse fermante manquante");
      p++;
      return a;
    }
    throw new ExprError(`« ${t.v} » inattendu à la position ${t.at + 1}`);
  }

  const root = expr(0);
  if (p < toks.length) throw new ExprError(`« ${toks[p].v} » inattendu à la position ${toks[p].at + 1}`);
  return root;
}

function collectVars(n: Node, out: Set<string>) {
  switch (n.t) {
    case "var": out.add(n.name); break;
    case "neg": case "call": collectVars(n.a, out); break;
    case "bin": collectVars(n.a, out); collectVars(n.b, out); break;
  }
  return out;
}

function evaluate(n: Node, v: Record<string, number>): number {
  switch (n.t) {
    case "num": return n.v;
    case "var": return v[n.name] ?? NaN;
    case "neg": return -evaluate(n.a, v);
    case "call": return FUNCTIONS[n.fn](evaluate(n.a, v));
    case "bin": {
      const a = evaluate(n.a, v), b = evaluate(n.b, v);
      switch (n.op) { case "+": return a + b; case "-": return a - b; case "*": return a * b; case "/": return a / b; case "^": return Math.pow(a, b); }
    }
  }
}

export type CompiledExpr = { fn: (vars: Record<string, number>) => number; vars: string[] };

/**
 * Compile une expression. `allowed` liste les variables permises (ex. ["x", "a", "b"]) :
 * toute autre lettre est refusée avec un message clair pour l'enseignant.
 */
export function compileExpr(src: string, allowed: readonly string[]): { ok: true; expr: CompiledExpr } | { ok: false; error: string } {
  try {
    const ast = parse(src.trim());
    const vars = [...collectVars(ast, new Set())];
    const unknown = vars.filter((x) => !allowed.includes(x));
    if (unknown.length) return { ok: false, error: `Variable inconnue : ${unknown.join(", ")} (disponibles : ${allowed.join(", ") || "aucune"})` };
    return { ok: true, expr: { fn: (v) => evaluate(ast, v), vars } };
  } catch (e) {
    if (e instanceof ExprError) return { ok: false, error: e.message };
    throw e;
  }
}
